'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import {
  ArrowLeft, Printer, Download, Pencil, FileText,
  CheckCircle2, Clock, Send, XCircle, Loader2
} from 'lucide-react';
import { Quotation } from '@/lib/types';
import { getQuotationById, getQuotations } from '@/lib/store';
import { formatINR, numberToWords } from '@/lib/calculations';
import AppImage from '@/components/ui/AppImage';

export default function QuotationPreviewContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const id = searchParams.get('id');
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      const q = getQuotationById(id);
      setQuotation(q || null);
    } else {
      // Use most recent
      const all = getQuotations();
      setQuotation(all[0] || null);
    }
    setLoading(false);
  }, [id]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownloadPDF = useCallback(async () => {
    setPrinting(true);
    toast.info('Preparing PDF...');
    await new Promise(r => setTimeout(r, 300));
    window.print();
    setPrinting(false);
    toast.success('Print dialog opened — save as PDF');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-[#E8620A]" />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 py-16 text-center">
        <FileText size={48} className="mx-auto text-slate-200 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">No quotation selected</h2>
        <p className="text-slate-500 mb-6 text-sm">Select a quotation from the management screen to preview it here.</p>
        <Link href="/quotations-management" className="inline-flex items-center gap-2 bg-[#E8620A] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#d05508] transition-colors">
          View All Quotations
        </Link>
      </div>
    );
  }

  const { subtotal, totalGST, grandTotal, gstType, lineItems } = quotation;
  const cgst = gstType === 'CGST_SGST' ? totalGST / 2 : 0;
  const sgst = gstType === 'CGST_SGST' ? totalGST / 2 : 0;
  const igst = gstType === 'IGST' ? totalGST : 0;

  // Group by GST rate for breakdown
  const gstBreakdown = lineItems.reduce((acc, item) => {
    const key = `${item.gstRate}`;
    if (!acc[key]) acc[key] = { rate: item.gstRate, taxable: 0, gstAmt: 0 };
    acc[key].taxable += item.amount;
    acc[key].gstAmt += item.gstAmount;
    return acc;
  }, {} as Record<string, { rate: number; taxable: number; gstAmt: number }>);

  const statusIcon = {
    Draft: <Clock size={14} className="text-slate-500" />,
    Sent: <Send size={14} className="text-blue-600" />,
    Approved: <CheckCircle2 size={14} className="text-green-600" />,
    Rejected: <XCircle size={14} className="text-red-600" />,
  }[quotation.status];

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-8">
      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6 no-print">
        <div className="flex items-center gap-3">
          <Link href="/quotations-management" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Quotation Preview</h1>
            <p className="text-sm text-slate-500">{quotation.quoteNumber} — {quotation.clientCompany}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            href={`/quotation-editor?id=${quotation.id}`}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Pencil size={14} />
            Edit
          </Link>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 border border-[#1B2A4A] text-[#1B2A4A] rounded-lg text-sm font-semibold hover:bg-[#1B2A4A]/5 transition-all active:scale-95"
          >
            <Printer size={14} />
            Print
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={printing}
            className="flex items-center gap-2 bg-[#E8620A] hover:bg-[#d05508] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95 disabled:opacity-70"
          >
            {printing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download PDF
          </button>
        </div>
      </div>

      {/* A4 Preview Container */}
      <div className="flex justify-center">
        <div
          ref={printRef}
          id="print-quotation"
          className="print-area bg-white shadow-2xl"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '0',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {/* ===== LETTERHEAD HEADER IMAGE ===== */}
          <div className="w-full" style={{ height: '120px', position: 'relative', overflow: 'hidden' }}>
            <AppImage
              src="/assets/images/WhatsApp_Image_2026-04-25_at_1.55.52_PM-1777274914114.jpeg"
              alt="PEAKTIME letterhead header with orange gold navy geometric design and company logo"
              fill
              style={{ objectFit: 'cover', objectPosition: 'top' }}
              priority
            />
          </div>

          {/* ===== QUOTATION BODY ===== */}
          <div style={{ padding: '16px 28px 20px 28px' }}>

            {/* Quote title bar */}
            <div style={{
              background: 'linear-gradient(135deg, #1B2A4A 0%, #243556 100%)',
              borderRadius: '6px',
              padding: '8px 16px',
              marginBottom: '14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase' }}>
                  Quotation
                </span>
                <span style={{ color: '#C9922A', fontWeight: 600, fontSize: '12px', marginLeft: '12px' }}>
                  {quotation.quoteNumber}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                <span style={{ color: '#94A3B8', fontSize: '10px' }}>Date: <span style={{ color: '#E2E8F0' }}>{quotation.date}</span></span>
                <span style={{ color: '#94A3B8', fontSize: '10px' }}>Valid Until: <span style={{ color: '#E2E8F0' }}>{quotation.validUntil}</span></span>
              </div>
            </div>

            {/* Subject */}
            <div style={{ marginBottom: '12px', borderLeft: '3px solid #E8620A', paddingLeft: '10px' }}>
              <p style={{ fontSize: '10px', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</p>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#1B2A4A', marginTop: '2px' }}>{quotation.subject}</p>
            </div>

            {/* Parties */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              {/* From */}
              <div style={{ background: '#F8F9FB', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 12px' }}>
                <p style={{ fontSize: '9px', fontWeight: 700, color: '#E8620A', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>From</p>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#1B2A4A' }}>PEAKTIME</p>
                <p style={{ fontSize: '10px', color: '#475569', marginTop: '3px', lineHeight: '1.5' }}>
                  26/5 H1 block Qutab Vihar Ph-1,<br />
                  Dwarka Sector 19, New Delhi,<br />
                  India - 110071
                </p>
                <p style={{ fontSize: '10px', color: '#475569', marginTop: '4px' }}>
                  📞 +91 9818622951 / +91 9312709451
                </p>
                <p style={{ fontSize: '10px', color: '#475569' }}>🌐 www.peaktime.in</p>
              </div>

              {/* To */}
              <div style={{ background: '#F8F9FB', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 12px' }}>
                <p style={{ fontSize: '9px', fontWeight: 700, color: '#E8620A', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>To</p>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#1B2A4A' }}>{quotation.clientCompany}</p>
                <p style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{quotation.clientName}</p>
                {quotation.clientAddress && (
                  <p style={{ fontSize: '10px', color: '#475569', marginTop: '3px', lineHeight: '1.5' }}>{quotation.clientAddress}</p>
                )}
                {quotation.clientPhone && (
                  <p style={{ fontSize: '10px', color: '#475569', marginTop: '3px' }}>📞 {quotation.clientPhone}</p>
                )}
                {quotation.clientGSTIN && (
                  <p style={{ fontSize: '10px', color: '#475569', marginTop: '2px' }}>GSTIN: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{quotation.clientGSTIN}</span></p>
                )}
                {quotation.preparedBy && (
                  <p style={{ fontSize: '10px', color: '#94A3B8', marginTop: '4px' }}>Prepared by: {quotation.preparedBy}</p>
                )}
              </div>
            </div>

            {/* Line Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', fontSize: '10px', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#1B2A4A' }}>
                  {['#', 'Description', 'HSN/SAC', 'Qty', 'Unit', 'Rate (₹)', 'GST%', 'Taxable (₹)', 'GST Amt (₹)', 'Total (₹)'].map((h, i) => (
                    <th
                      key={`print-th-${i}`}
                      style={{
                        padding: '6px 4px',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: '8px',
                        textAlign: i === 0 ? 'center' : i >= 3 ? 'right' : 'left',
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        borderRight: i < 9 ? '1px solid #243556' : 'none',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr
                    key={item.id}
                    style={{ background: idx % 2 === 0 ? '#FFFFFF' : '#F8F9FB', borderBottom: '1px solid #E2E8F0' }}
                  >
                    <td style={{ padding: '4px', textAlign: 'center', color: '#64748B', borderRight: '1px solid #E2E8F0', fontSize: '9px' }}>{item.srNo}</td>
                    <td style={{ padding: '4px', color: '#1B2A4A', fontWeight: 500, borderRight: '1px solid #E2E8F0', fontSize: '9px', wordBreak: 'break-word', overflow: 'hidden' }}>{item.description}</td>
                    <td style={{ padding: '4px', fontFamily: 'monospace', color: '#64748B', borderRight: '1px solid #E2E8F0', fontSize: '8px', wordBreak: 'break-all' }}>{item.hsn}</td>
                    <td style={{ padding: '4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderRight: '1px solid #E2E8F0', fontSize: '9px' }}>{item.qty}</td>
                    <td style={{ padding: '4px', color: '#64748B', borderRight: '1px solid #E2E8F0', fontSize: '8px', textAlign: 'center' }}>{item.unit}</td>
                    <td style={{ padding: '4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderRight: '1px solid #E2E8F0', fontSize: '9px' }}>{item.rate.toLocaleString('en-IN')}</td>
                    <td style={{ padding: '4px', textAlign: 'center', borderRight: '1px solid #E2E8F0', fontSize: '9px' }}>{item.gstRate}%</td>
                    <td style={{ padding: '4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderRight: '1px solid #E2E8F0', fontSize: '9px' }}>{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td style={{ padding: '4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderRight: '1px solid #E2E8F0', fontSize: '9px' }}>{item.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td style={{ padding: '4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: '9px' }}>{item.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals + GST Breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              {/* GST Breakdown */}
              <div style={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{ background: '#1B2A4A', padding: '5px 10px' }}>
                  <p style={{ color: '#FFFFFF', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>GST Summary ({gstType === 'CGST_SGST' ? 'CGST + SGST' : 'IGST'})</p>
                </div>
                <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F8F9FB' }}>
                      <th style={{ padding: '4px 8px', textAlign: 'left', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E8F0' }}>GST Rate</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E8F0' }}>Taxable</th>
                      {gstType === 'CGST_SGST' ? (
                        <>
                          <th style={{ padding: '4px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E8F0' }}>CGST</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E8F0' }}>SGST</th>
                        </>
                      ) : (
                        <th style={{ padding: '4px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E8F0' }}>IGST</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(gstBreakdown).map(row => (
                      <tr key={`gst-row-${row.rate}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '3px 8px', color: '#475569' }}>{row.rate}%</td>
                        <td style={{ padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.taxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        {gstType === 'CGST_SGST' ? (
                          <>
                            <td style={{ padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(row.gstAmt / 2).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(row.gstAmt / 2).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </>
                        ) : (
                          <td style={{ padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.gstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Amount Summary */}
              <div style={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{ background: '#1B2A4A', padding: '5px 10px' }}>
                  <p style={{ color: '#FFFFFF', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount Summary</p>
                </div>
                <div style={{ padding: '8px 12px', fontSize: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px solid #F1F5F9', marginBottom: '4px' }}>
                    <span style={{ color: '#64748B' }}>Subtotal (Taxable)</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{formatINR(subtotal)}</span>
                  </div>
                  {gstType === 'CGST_SGST' ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                        <span style={{ color: '#64748B' }}>CGST</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatINR(cgst)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px solid #F1F5F9', marginBottom: '4px' }}>
                        <span style={{ color: '#64748B' }}>SGST</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatINR(sgst)}</span>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px solid #F1F5F9', marginBottom: '4px' }}>
                      <span style={{ color: '#64748B' }}>IGST</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatINR(igst)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#1B2A4A', margin: '0 -12px -8px -12px', padding: '8px 12px', borderRadius: '0 0 5px 5px' }}>
                    <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '11px' }}>Grand Total</span>
                    <span style={{ color: '#E8620A', fontWeight: 800, fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{formatINR(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Amount in words */}
            <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '6px', padding: '6px 12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '9px', fontWeight: 700, color: '#9A3412', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount in Words: </span>
              <span style={{ fontSize: '10px', color: '#7C2D12', fontStyle: 'italic' }}>{numberToWords(grandTotal)}</span>
            </div>

            {/* Terms & Notes */}
            <div style={{ display: 'grid', gridTemplateColumns: quotation.notes ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '14px' }}>
              {quotation.terms && (
                <div>
                  <p style={{ fontSize: '9px', fontWeight: 700, color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', borderBottom: '1px solid #E2E8F0', paddingBottom: '3px' }}>Terms & Conditions</p>
                  <p style={{ fontSize: '9px', color: '#475569', lineHeight: '1.6' }}>{quotation.terms}</p>
                </div>
              )}
              {quotation.notes && (
                <div>
                  <p style={{ fontSize: '9px', fontWeight: 700, color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', borderBottom: '1px solid #E2E8F0', paddingBottom: '3px' }}>Notes</p>
                  <p style={{ fontSize: '9px', color: '#475569', lineHeight: '1.6' }}>{quotation.notes}</p>
                </div>
              )}
            </div>

            {/* Signature + Seal */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '16px' }}>
              <div style={{ borderTop: '1px dashed #CBD5E1', paddingTop: '8px' }}>
                <p style={{ fontSize: '9px', color: '#64748B', textAlign: 'center' }}>Authorised Signatory</p>
                <p style={{ fontSize: '10px', fontWeight: 600, color: '#1B2A4A', textAlign: 'center', marginTop: '2px' }}>PEAKTIME</p>
              </div>
              <div style={{ borderTop: '1px dashed #CBD5E1', paddingTop: '8px' }}>
                <p style={{ fontSize: '9px', color: '#64748B', textAlign: 'center' }}>Client Signature & Stamp</p>
                <p style={{ fontSize: '10px', fontWeight: 600, color: '#1B2A4A', textAlign: 'center', marginTop: '2px' }}>{quotation.clientCompany}</p>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              marginTop: '16px',
              background: 'linear-gradient(135deg, #1B2A4A 0%, #E8620A 100%)',
              borderRadius: '6px',
              padding: '8px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ color: '#FFFFFF', fontSize: '9px', fontWeight: 500 }}>
                26/5 H1 block Qutab Vihar Ph-1, Dwarka Sector 19, New Delhi - 110071
              </span>
              <span style={{ color: '#FFD7A8', fontSize: '9px', fontWeight: 600 }}>
                +91 9818622951 | +91 9312709451 | www.peaktime.in
              </span>
            </div>

            {/* Page note */}
            <p style={{ textAlign: 'center', fontSize: '8px', color: '#94A3B8', marginTop: '8px' }}>
              This is a computer-generated quotation and does not require a physical signature. | Generated on {new Date().toLocaleDateString('en-GB')}
            </p>
          </div>
        </div>
      </div>

      {/* Print styles injected inline - Cross-platform compatible */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0mm;
          }
          html, body {
            width: 210mm !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body * { 
            visibility: hidden !important; 
          }
          #print-quotation, 
          #print-quotation * { 
            visibility: visible !important; 
          }
          #print-quotation {
            position: absolute !important;
            left: 0 !important; 
            top: 0 !important;
            width: 210mm !important;
            min-height: auto !important;
            max-width: 210mm !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          #print-quotation table {
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
          }
          #print-quotation thead {
            display: table-header-group !important;
          }
          #print-quotation tbody {
            display: table-row-group !important;
          }
          #print-quotation tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
          }
          #print-quotation td,
          #print-quotation th {
            overflow: hidden !important;
            word-wrap: break-word !important;
            word-break: break-word !important;
          }
          .no-print { 
            display: none !important; 
          }
        }
      `}</style>
    </div>
  );
}
